import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
// Direct file import, not the `shared/components/common` barrel — the barrel pulls
// in auth-context consumers that this unauthenticated wizard step must not require.
import { CollapsibleSection } from '../../../shared/components/common/CollapsibleSection';
import { ModuleMaturityBadge } from '../../../shared/components/ModuleMaturityBadge';
import { getIcon } from '../../../shared/config/iconMap';
import { getModuleById } from '../../../shared/config/modules.config';
import type { SetupModule } from '../../../shared/api/setupWizardApi';
import { WIZARD_VISIBILITY_MODES, type WizardVisibilityMode } from './options';

interface ModulesStepProps {
  modules: SetupModule[];
  selectedModules: string[];
  recommendedModules: string[];
  /**
   * Alpha modules the same rules matched. They are shown inside the collapsed
   * experimental section and marked, but never pre-selected — the backend keeps
   * them out of `recommendedModules` for exactly that reason.
   */
  suggestedModules: string[];
  hasRoleInput: boolean;
  onChange: (modules: string[]) => void;
  /** Per-object_type visibility mode chosen for configurable selected modules. */
  visibilityModes: Record<string, string>;
  onVisibilityChange: (objectType: string, mode: string) => void;
  /**
   * Mode applied to any module the admin hasn't answered — derived from the
   * company size band on Step 1. Rendering it as the selected radio keeps the UI
   * honest: what's checked here is exactly what the backend will seed.
   */
  defaultVisibilityMode: WizardVisibilityMode;
}

export function ModulesStep({
  modules,
  selectedModules,
  recommendedModules,
  suggestedModules,
  hasRoleInput,
  onChange,
  visibilityModes,
  onVisibilityChange,
  defaultVisibilityMode,
}: ModulesStepProps) {
  const { t } = useTranslation(['user_management', 'common']);

  const toggleModule = (moduleId: string) => {
    onChange(
      selectedModules.includes(moduleId)
        ? selectedModules.filter((m) => m !== moduleId)
        : [...selectedModules, moduleId]
    );
  };

  // Maturity comes from the same module config the module picker and home page
  // read, so a module's β/α marker can never drift between the two surfaces.
  const isAlpha = (module: SetupModule) => getModuleById(module.id)?.status === 'alpha';

  // Experimental (alpha) modules never lead: they sit below a fold of their own so
  // the grid a new admin reads is the one they can rely on. Recommended modules
  // lead the rest, so the shortlist we derived from their answers comes before the
  // full catalog. Backend order is preserved within each group.
  const stable = modules.filter((m) => !isAlpha(m));
  const experimental = modules.filter(isAlpha);
  const recommended = stable.filter((m) => recommendedModules.includes(m.id));
  const others = stable.filter((m) => !recommendedModules.includes(m.id));

  // Selected modules that expose a tenant-wide visibility mode (only these get a
  // wizard question). Config-driven via the backend `visibility_object_type`.
  const configurableSelected = modules.filter(
    (m) => m.visibility_object_type && selectedModules.includes(m.id)
  );

  // The collapsed summary must describe the modes actually chosen, not the default:
  // the step unmounts when the admin moves on, so a re-entered step re-collapses and
  // would otherwise claim the band default over a mode they had changed.
  const hasNonDefaultVisibility = configurableSelected.some(
    (m) =>
      (visibilityModes[m.visibility_object_type as string] ?? defaultVisibilityMode) !==
      defaultVisibilityMode
  );

  const renderModuleCard = (module: SetupModule) => {
    const icon = getIcon(module.icon);
    const isSelected = selectedModules.includes(module.id);
    // An alpha module is never "Recommended", whatever the server says: the badge
    // is the card-level half of the same contract the grid partition enforces, so
    // a stray alpha id in `recommendedModules` cannot render a card claiming to be
    // recommended while sitting inside the experimental fold.
    const isRecommended = !isAlpha(module) && recommendedModules.includes(module.id);
    const isSuggested = suggestedModules.includes(module.id);
    const status = getModuleById(module.id)?.status;
    return (
      <div
        key={module.id}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        onClick={() => toggleModule(module.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleModule(module.id);
          }
        }}
        className={cn(
          'flex cursor-pointer items-start gap-3 rounded-md border p-4 text-start transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
        )}
      >
        <Checkbox checked={isSelected} className="pointer-events-none mt-0.5" tabIndex={-1} />
        {icon && <span className="mt-0.5 text-muted-foreground">{icon}</span>}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium">{t(module.name_key, { ns: 'common' })}</span>
            {status && <ModuleMaturityBadge status={status} />}
            {isRecommended && (
              <Badge variant="secondary" className="py-0 text-xs">
                {t('setupWizardV2.modules.recommended')}
              </Badge>
            )}
            {isSuggested && (
              <Badge variant="secondary" className="py-0 text-xs">
                {t('setupWizardV2.modules.suggested')}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t(module.description_key, { ns: 'common' })}
          </p>
        </div>
      </div>
    );
  };

  const renderGrid = (group: SetupModule[]) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{group.map(renderModuleCard)}</div>
  );

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {hasRoleInput
          ? t('setupWizardV2.modules.descriptionWithRecommendations')
          : t('setupWizardV2.modules.description')}
      </p>

      {recommended.length > 0 ? (
        <>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">
              {t('setupWizardV2.modules.recommendedHeading')}
            </h3>
            {renderGrid(recommended)}
          </div>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">{t('setupWizardV2.modules.moreHeading')}</h3>
            {renderGrid(others)}
          </div>
        </>
      ) : (
        renderGrid(others)
      )}

      {/* Experimental (alpha) modules, below a fold of their own. Collapsed and
          never pre-selected: an alpha module is incomplete by definition, so a
          tenant only ever gets one by opening this and asking for it. The rules
          still mark the ones that fit the company ("Suggested"), which is why the
          warning is repeated INSIDE the section rather than only on the trigger. */}
      {experimental.length > 0 && (
        <div className="border-t pt-6">
          <CollapsibleSection
            label={t('setupWizardV2.modules.experimentalHeading')}
            summary={t('setupWizardV2.modules.experimentalSummary')}
          >
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <p className="text-xs text-warning">
                {t('setupWizardV2.modules.experimentalWarning')}
              </p>
            </div>
            {renderGrid(experimental)}
          </CollapsibleSection>
        </div>
      )}

      {/* Per-module default visibility (Sharing Infra P-4). Collapsed by default —
          the untouched state already means whatever the company's size band implies
          (small companies share everything; medium-and-up start restricted), so a new
          admin never has to answer a sharing-policy question during onboarding. */}
      {configurableSelected.length > 0 && (
        <div className="border-t pt-6">
          <CollapsibleSection
            label={t('setupWizardV2.visibility.advancedHeading')}
            summary={
              hasNonDefaultVisibility
                ? t('setupWizardV2.visibility.collapsedSummaryCustomized')
                : t(`setupWizardV2.visibility.collapsedSummary.${defaultVisibilityMode}`)
            }
          >
            <p className="text-sm text-muted-foreground">
              {t('setupWizardV2.visibility.description')}
            </p>
            {configurableSelected.map((module) => {
              const objectType = module.visibility_object_type as string;
              const value = visibilityModes[objectType] ?? defaultVisibilityMode;
              return (
                <div key={module.id} className="space-y-2">
                  <span className="text-sm font-medium">{t(module.name_key, { ns: 'common' })}</span>
                  <RadioGroup
                    value={value}
                    onValueChange={(v) => onVisibilityChange(objectType, v)}
                    className="space-y-2"
                  >
                    {WIZARD_VISIBILITY_MODES.map((mode) => (
                      <div key={mode} className="flex items-start space-x-3">
                        <RadioGroupItem
                          value={mode}
                          id={`wizard_vis_${objectType}_${mode}`}
                          className="mt-1"
                        />
                        <div className="space-y-0.5">
                          <Label
                            htmlFor={`wizard_vis_${objectType}_${mode}`}
                            className="cursor-pointer"
                          >
                            {t(`common:visibilityMode.${mode}.label`)}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {t(`common:visibilityMode.${mode}.description`)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              );
            })}
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
}
